import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  BugReport,
  AutoAwesome,
  Speed,
  Security,
  Palette,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface ChangelogDialogProps {
  open: boolean;
  onClose: () => void;
  version: string;
}

const ChangelogDialog: React.FC<ChangelogDialogProps> = ({ open, onClose, version }) => {
  const { t, i18n } = useTranslation();

  const changelogEn = {
    "2.3.7": {
      date: "2025-07-22",
      changes: [
        {
          type: "fix",
          category: "Bug Fixes",
          items: [
            "Fixed hover lag on project and team member cards",
            "Fixed login streak calculation logic",
            "Fixed time tracking data display issues",
            "Fixed time tracking edit form resetting to 0",
            "Fixed Accept-Encoding header error in API requests",
            "Created backend API for inspiration items to prevent data loss"
          ]
        },
        {
          type: "enhancement",
          category: "Performance",
          items: [
            "Optimized hover animations with GPU acceleration",
            "Improved time entry duration validation",
            "Added migration to fix invalid time entry durations"
          ]
        }
      ]
    },
    "2.3.6": {
      date: "2025-07-13",
      changes: [
        {
          type: "enhancement",
          category: "UI/UX Improvements",
          items: [
            "Fixed user role display formatting (media_buyer → Media Buyer)",
            "Removed unnecessary white activity button from team dashboard",
            "Added missing translations for tasks.viewAll and teams actions",
            "Improved Research page with optional supplier link and sale price",
            "Added Daily Sales field to research items",
            "Enhanced team member sorting by online status (online first)",
            "Added personal login streak display for superadmins",
            "Created comprehensive project details page with task management"
          ]
        },
        {
          type: "feature",
          category: "New Features",
          items: [
            "Added Inspiration tab to Research page for ad image/video uploads",
            "Enhanced project details with overview dashboard and metrics",
            "Implemented tabbed interface for project management (Tasks, Team, Activity)",
            "Added proper navigation between projects list and individual project details"
          ]
        },
        {
          type: "fix",
          category: "Bug Fixes",
          items: [
            "Fixed Creative creation display issue in Sales page",
            "Fixed translation inconsistencies in French mode",
            "Updated shop cards to show yesterday's data only (matching Total PnL logic)",
            "Resolved missing translation keys causing display issues"
          ]
        }
      ]
    },
    "2.3.5": {
      date: "2025-07-12", 
      changes: [
        {
          type: "feature",
          category: "Core Features",
          items: [
            "Enhanced sales dashboard with better data visualization",
            "Improved team activity tracking",
            "Added real-time online status indicators"
          ]
        }
      ]
    }
  };

  const changelogFr = {
    "2.3.7": {
      date: "2025-07-22",
      changes: [
        {
          type: "fix",
          category: "Corrections de bugs",
          items: [
            "Correction du lag au survol des cartes de projets et membres d'équipe",
            "Correction du calcul de la série de connexions",
            "Correction des problèmes d'affichage des données de suivi du temps",
            "Correction du formulaire d'édition du suivi du temps qui se réinitialisait à 0",
            "Correction de l'erreur d'en-tête Accept-Encoding dans les requêtes API",
            "Création d'une API backend pour les éléments d'inspiration pour éviter la perte de données"
          ]
        },
        {
          type: "enhancement",
          category: "Performance",
          items: [
            "Optimisation des animations de survol avec accélération GPU",
            "Amélioration de la validation de la durée des entrées de temps",
            "Ajout d'une migration pour corriger les durées d'entrée de temps invalides"
          ]
        }
      ]
    },
    "2.3.6": {
      date: "2025-07-13",
      changes: [
        {
          type: "enhancement",
          category: "Améliorations UI/UX",
          items: [
            "Correction de l'affichage des rôles utilisateur (media_buyer → Media Buyer)",
            "Suppression du bouton d'activité blanc inutile du tableau de bord équipe",
            "Ajout des traductions manquantes pour tasks.viewAll et les actions teams",
            "Amélioration de la page Recherche avec lien fournisseur et prix de vente optionnels",
            "Ajout du champ Ventes quotidiennes aux éléments de recherche",
            "Amélioration du tri des membres d'équipe par statut en ligne (en ligne en premier)",
            "Ajout de l'affichage des séries de connexion personnelles pour les superadmins",
            "Création d'une page de détails de projet complète avec gestion des tâches"
          ]
        },
        {
          type: "feature",
          category: "Nouvelles Fonctionnalités",
          items: [
            "Ajout de l'onglet Inspiration à la page Recherche pour télécharger images/vidéos pub",
            "Amélioration des détails de projet avec tableau de bord de vue d'ensemble et métriques",
            "Implémentation d'interface à onglets pour la gestion de projet (Tâches, Équipe, Activité)",
            "Ajout de la navigation appropriée entre la liste des projets et les détails individuels"
          ]
        },
        {
          type: "fix",
          category: "Corrections de Bugs",
          items: [
            "Correction du problème d'affichage de création de Creative dans la page Ventes",
            "Correction des incohérences de traduction en mode français",
            "Mise à jour des cartes boutique pour afficher seulement les données d'hier (logique Total PnL)",
            "Résolution des clés de traduction manquantes causant des problèmes d'affichage"
          ]
        }
      ]
    },
    "2.3.5": {
      date: "2025-07-12", 
      changes: [
        {
          type: "feature",
          category: "Fonctionnalités Principales",
          items: [
            "Amélioration du tableau de bord des ventes avec une meilleure visualisation des données",
            "Amélioration du suivi d'activité de l'équipe",
            "Ajout d'indicateurs de statut en ligne en temps réel"
          ]
        }
      ]
    }
  };

  const changelog = i18n.language === 'fr' ? changelogFr : changelogEn;

  const getIcon = (type: string) => {
    switch (type) {
      case 'feature':
        return <AutoAwesome sx={{ color: 'primary.main' }} />;
      case 'enhancement': 
        return <Speed sx={{ color: 'success.main' }} />;
      case 'fix':
        return <BugReport sx={{ color: 'warning.main' }} />;
      case 'security':
        return <Security sx={{ color: 'error.main' }} />;
      default:
        return <CheckCircle sx={{ color: 'success.main' }} />;
    }
  };

  const getChipColor = (type: string) => {
    switch (type) {
      case 'feature':
        return 'primary';
      case 'enhancement':
        return 'success';
      case 'fix':
        return 'warning';
      case 'security':
        return 'error';
      default:
        return 'default';
    }
  };

  const currentChangelog = changelog[version as keyof typeof changelog];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight={600}>
            🚀 {i18n.language === 'fr' ? 'Nouveautés dans ASF Panel v' : 'What\'s New in ASF Panel v'}{version}
          </Typography>
          <Chip 
            label={currentChangelog?.date || 'Latest'} 
            variant="outlined" 
            size="small" 
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {currentChangelog ? (
          <Box>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {i18n.language === 'fr' 
                ? 'Nous améliorons constamment ASF Panel. Voici les nouveautés de cette version :'
                : 'We\'re constantly improving ASF Panel. Here\'s what\'s new in this version:'
              }
            </Typography>
            
            {currentChangelog.changes.map((section, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {getIcon(section.type)}
                  <Typography variant="h6" fontWeight={600}>
                    {section.category}
                  </Typography>
                  <Chip 
                    label={section.type} 
                    size="small" 
                    color={getChipColor(section.type) as any}
                    variant="outlined"
                  />
                </Box>
                
                <List dense sx={{ pl: 2 }}>
                  {section.items.map((item, itemIndex) => (
                    <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
                
                {index < currentChangelog.changes.length - 1 && <Divider sx={{ my: 2 }} />}
              </Box>
            ))}
            
            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                💡 <strong>{i18n.language === 'fr' ? 'Commentaires' : 'Feedback'}:</strong> {
                  i18n.language === 'fr' 
                    ? 'Nous aimerions connaître votre avis ! Si vous avez des suggestions ou rencontrez des problèmes, n\'hésitez pas à contacter l\'équipe de développement.'
                    : 'We\'d love to hear your thoughts! If you have suggestions or encounter any issues, please reach out to the development team.'
                }
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No changelog available for version {version}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {i18n.language === 'fr' ? 'Compris !' : 'Got it!'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangelogDialog;